import { useEffect, useState } from "react"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"

export type ToastVariant = "default" | "destructive" | "success"

export interface ToastData {
  id: string
  title?: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 5000

let toastListeners: Array<(toasts: ToastData[]) => void> = []
let toastQueue: ToastData[] = []
let toastCount = 0

function addToast(toast: Omit<ToastData, "id">) {
  const id = `toast-${++toastCount}`
  const newToast = { ...toast, id }
  
  toastQueue = [...toastQueue, newToast].slice(-TOAST_LIMIT)
  toastListeners.forEach((listener) => listener(toastQueue))
  
  setTimeout(() => {
    removeToast(id)
  }, toast.duration || TOAST_REMOVE_DELAY)
  
  return id
}

function removeToast(id: string) {
  toastQueue = toastQueue.filter((t) => t.id !== id)
  toastListeners.forEach((listener) => listener(toastQueue))
}

// eslint-disable-next-line react-refresh/only-export-components
export function toast(props: Omit<ToastData, "id">) {
  return addToast(props)
}

toast.success = (title: string, description?: string) => {
  return addToast({ title, description, variant: "success" })
}

toast.error = (title: string, description?: string) => {
  return addToast({ title, description, variant: "destructive" })
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const [toasts, setToasts] = useState<ToastData[]>(toastQueue)

  useEffect(() => {
    toastListeners.push(setToasts)
    return () => {
      toastListeners = toastListeners.filter((l) => l !== setToasts)
    }
  }, [])

  return {
    toasts,
    toast,
    dismiss: removeToast,
  }
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, variant }) => (
        <Toast key={id} variant={variant}>
          <div className="grid gap-1">
            {title && <ToastTitle>{title}</ToastTitle>}
            {description && <ToastDescription>{description}</ToastDescription>}
          </div>
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  )
}
